
import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import type { ClientSubscriptionStore, ReturnedData } from './types';
import { toast } from '@/hooks/use-toast';
import { Subscription } from '@/types/subscription';
import { mapDbSubscriptionToDomain } from '@/utils/subscriptionMapper';
import { getUserGymId } from '@/utils/gymAuth';
import { getUserSubscriptions, createSubscription, updateSubscription } from '@/services/edgeFunctionWrappers/subscriptionsManagementWrapper';
import { getPayments } from '@/services/edgeFunctionWrappers/paymentsManagementWrapper';
import { subscriptionsCoverageOverlap } from '@/lib/subscriptionCalendars';
import i18n from '@/i18n';

/**
 * Parmi les abonnements actifs, ceux que le nouvel abonnement RECOUVRE et qui
 * doivent donc être clôturés avant l'insertion.
 *
 * Le critère est celui du module partagé (`subscriptionsCoverageOverlap`),
 * c'est-à-dire celui qu'appliquera la fonction edge : deux abonnements ne
 * peuvent coexister que si leurs plannings sont disjoints. Ce qui ne recoupe
 * pas le nouveau reste actif — c'est tout l'objet du cumul.
 *
 * Ne lève jamais. Si les liens `type -> calendriers` ne peuvent pas être lus,
 * on rend TOUS les abonnements actifs : le doute joue pour la clôture, donc
 * pour le comportement historique, jamais pour un cumul qu'on n'a pas pu
 * justifier (le serveur refuserait ensuite l'insertion, laissant l'adhérent
 * sans rien de neuf mais avec son ancien abonnement clôturé pour rien).
 */
async function resolveSubscriptionsToExpire(
  activeSubscriptions: any[],
  candidateTypeId: string | null
): Promise<any[]> {
  if (activeSubscriptions.length === 0) return [];

  const typeIds = Array.from(
    new Set(
      [...activeSubscriptions.map((s) => s.subscription_type_id), candidateTypeId].filter(
        (id): id is string => !!id
      )
    )
  );

  const linksByType = new Map<string, string[]>();
  if (typeIds.length > 0) {
    const { data: linkRows, error: linkError } = await supabase
      .from('subscription_type_calendars')
      .select('subscription_type_id, calendar_id')
      .in('subscription_type_id', typeIds);
    if (linkError) {
      console.error(
        '[clientSubscriptionStore] Lecture des plannings impossible, clôture de tous les abonnements actifs :',
        linkError
      );
      return activeSubscriptions;
    }
    for (const row of linkRows ?? []) {
      const list = linksByType.get(row.subscription_type_id) ?? [];
      list.push(row.calendar_id);
      linksByType.set(row.subscription_type_id, list);
    }
  }

  const scoped = (typeId: string | null) => ({
    subscription_type_id: typeId,
    calendar_ids: typeId ? linksByType.get(typeId) ?? [] : [],
  });
  const candidate = scoped(candidateTypeId);

  return activeSubscriptions.filter((s) =>
    subscriptionsCoverageOverlap(candidate, scoped(s.subscription_type_id ?? null))
  );
}



export const useClientSubscriptionStore = create<ClientSubscriptionStore>((set,get) => ({
  isLoading: false,
  subscription: null,
  allSubscriptions: [],
  billingHistory: [],
  paymentsBySub: {},
  
  
  async loadClientSubscriptionDataForUser(userId: string): Promise<ReturnedData> {
    if (!userId) {
      return { subscription: null, allSubscriptions: [], paymentsBySub: {}, billingHistory: [] };
    }

    const gymId = getUserGymId();
    
    // 1) Subscriptions du client via edge function wrapper
    const subscriptions = await getUserSubscriptions(userId, undefined, gymId);
    const mappedSubs = (subscriptions ?? []).map(mapDbSubscriptionToDomain);
    const active = mappedSubs?.find((s) => s.status === "Active") ?? null;

    // 2) Paiements du client
    const { data: payments, error: payErr } = await getPayments({
      gym_id: gymId,
      user_id: userId,
    });

    if (payErr) throw payErr;

    // 3) Indexation des paiements par abonnement
    const paymentsBySub: Record<string, any[]> = {};
    (payments ?? []).forEach((p) => {
      const key = p.subscription_id ?? "__no_sub__";
      if (!paymentsBySub[key]) paymentsBySub[key] = [];
      paymentsBySub[key].push(p as any);
    });

    // 4) Historique (limité) du plan actif
    const billingHistory = active ? (paymentsBySub[active.id] ?? []).slice(0, 5) : [];
    return {
      subscription: active,
      allSubscriptions: mappedSubs,
      paymentsBySub,
      billingHistory,
    };
  },

  loadClientSubscriptionData: async () => {
    try {
      set({ isLoading: true });

      const { data: auth } = await supabase.auth.getUser();
      const user = auth?.user;
      if (!user) throw new Error("No user found");
      // Appels en parallèle
      const {
        allSubscriptions,
        subscription,
        billingHistory,
        paymentsBySub,
      } = await get().loadClientSubscriptionDataForUser(user.id);

      set({
        allSubscriptions,
        subscription,
        billingHistory,
        paymentsBySub,
      });
    } catch (error) {
      console.error("Error loading subscription data:", error);
      toast({
        title: i18n.t("clientSubscriptionStore.loadErrorTitle", { ns: "adminui3" }),
        description: i18n.t("clientSubscriptionStore.loadErrorDescription", { ns: "adminui3" }),
        variant: "destructive",
      });
    } finally {
      set({ isLoading: false });
    }
  },
  extendActiveSubscriptionForUser: async (userId, { additionalSessions = 0, additionalJokers = 0, additionalDays = 0 }) => {
  try {
    set({ isLoading: true });
    console.log('Extending active subscription for user:', userId, additionalSessions, additionalJokers, additionalDays);

    if (!userId) {
      console.error('Invalid user ID:', userId);
      toast({
        title: "Error",
        description: i18n.t("clientSubscriptionStore.invalidUserId", { ns: "adminui3" }),
        variant: "destructive"
      });
      set({ isLoading: false });
      return false;
    }

    const userSubscription = await get().loadClientSubscriptionDataForUser(userId);
  
    if (!userSubscription) {
      console.error('No active subscription found for user:', userId);
      toast({
        title: "Error",
        description: i18n.t("clientSubscriptionStore.noActiveSubscription", { ns: "adminui3" }),
        variant: "destructive"
      });
      set({ isLoading: false });
      return false;
    }

    const subscription = userSubscription.subscription ?? userSubscription.allSubscriptions[0];
    const subscriptionId = subscription.id;

    console.log('Found active subscription:', subscriptionId);

    // 2️⃣ Calculer les nouvelles valeurs
    const updatedSessions = (subscription.sessions_remaining ?? 0) + additionalSessions;
    const updatedJokers = (subscription.jokers_remaining ?? 0) + additionalJokers;
     const updatedSessionsTot = (subscription.sessions_total ?? 0) + additionalSessions;
    const updatedJokersTot = (subscription.jokers_total ?? 0) + additionalJokers;


    let updatedEndDate = subscription.end_date;
    if (additionalDays > 0 && subscription.end_date) {
      const endDateObj = new Date(subscription.end_date);
      endDateObj.setDate(endDateObj.getDate() + additionalDays);
      updatedEndDate = endDateObj.toISOString();
    }

    // 3️⃣ Appliquer la mise à jour via edge function wrapper
    try {
      await updateSubscription(subscriptionId, {
        sessions_remaining: updatedSessions,
        jokers_remaining: updatedJokers,
        end_date: updatedEndDate,
        sessions_total: updatedSessionsTot,
        jokers_total: updatedJokersTot,
        status: 'active'
      }, getUserGymId());
    } catch (updateError: any) {
      console.error('Error updating subscription:', updateError);
      toast({
        title: "Error",
        description: i18n.t("clientSubscriptionStore.extendFailed", { ns: "adminui3", message: updateError.message }),
        variant: "destructive"
      });
      set({ isLoading: false });
      return false;
    }

    console.log('Subscription extended successfully');
    toast({
      title: i18n.t("clientSubscriptionStore.extendSuccessTitle", { ns: "adminui3" }),
      description: i18n.t("clientSubscriptionStore.extendSuccessDescription", { ns: "adminui3" })
    });
    set({ isLoading: false });
    return true;
  } catch (error) {
    console.error('Error extending subscription:', error);
    toast({
      title: "Error",
      description: i18n.t("clientSubscriptionStore.unexpectedError", { ns: "adminui3" }),
      variant: "destructive"
    });
    set({ isLoading: false });
    return false;
  }
},
updateSubscriptionOnly: async (userId: string, subscriptionData: Subscription, forceCreateNewSubscriptionOnOpen:boolean) => {
  // Protection contre les double-clics
  set({ isLoading: true });
  
  try {
    if (!userId) {
      console.error("Invalid user ID");
      toast({
        title: i18n.t("clientSubscriptionStore.errorTitle", { ns: "adminui3" }),
        description: i18n.t("clientSubscriptionStore.invalidUserIdFr", { ns: "adminui3" }),
        variant: "destructive",
      });
      return false;
    }
    const gymId = getUserGymId();

    // 1) Récupère les abonnements actifs courants.
    //
    // Le `.limit(1)` d'origine est levé : un adhérent peut désormais en détenir
    // plusieurs, de plannings disjoints. `subscriptions[0]` reste le plus
    // récent, donc le cas A (modification de l'actif) est inchangé ; c'est le
    // cas B qui a besoin de la liste complète pour ne clôturer que ce qu'il
    // faut (voir plus bas).
    const { data: subscriptions, error: subscriptionError } = await supabase
      .from("subscriptions")
      .select("*")
      .eq("user_id", userId)
      .eq("status", "active")
      .eq('gym_id', gymId)
      .order("created_at", { ascending: false });

    if (subscriptionError) {
      console.error("Error fetching active subscription:", subscriptionError);
      toast({
        title: i18n.t("clientSubscriptionStore.errorTitle", { ns: "adminui3" }),
        description: i18n.t("clientSubscriptionStore.fetchActiveError", { ns: "adminui3" }),
        variant: "destructive",
      });
      return false;
    }

    const activeSubscriptions = subscriptions ?? [];
    const currentActive = activeSubscriptions[0] ?? null;

    // 2) Cas A : modification de l'abonnement actif.
    //
    // La condition portait sur `subscriptionData.id`, que l'appelant
    // (ClientSubscriptionDialog) ne renseignait jamais : on tombait toujours
    // dans la branche d'insertion, ce qui créait un second abonnement actif
    // sans expirer l'ancien. Dès qu'on n'a pas explicitement demandé un
    // nouvel abonnement, un actif existant est donc modifié — quel que soit
    // l'`id` fourni, à condition qu'il ne désigne pas un *autre* abonnement.
    const targetsCurrentActive = !subscriptionData.id || subscriptionData.id === currentActive?.id;

    if (currentActive && targetsCurrentActive && !forceCreateNewSubscriptionOnOpen) {
      // On n'écrase ni la clé primaire, ni le propriétaire, ni la salle.
      const { id: _ignoredId, user_id: _ignoredUser, gym_id: _ignoredGym, status, ...fields } = subscriptionData;
      const updatePayload = {
        ...fields,
        // Le spread venait *après* le statut minusculé et le réécrasait par
        // « Active » : la colonne attend une valeur en minuscules.
        status: String(status ?? "active").toLowerCase(),
      };
      const { error: updateError } = await supabase
        .from("subscriptions")
        .update(updatePayload)
        .eq("id", currentActive.id);

      if (updateError) {
        console.error("Erreur de mise à jour :", updateError);
        toast({
          title: i18n.t("clientSubscriptionStore.errorTitle", { ns: "adminui3" }),
          description: i18n.t("clientSubscriptionStore.updateError", { ns: "adminui3", message: updateError.message }),
          variant: "destructive",
        });
        return false;
      }

      console.log("Subscription updated successfully for", userId);
      toast({
        title: i18n.t("clientSubscriptionStore.updateSuccessTitle", { ns: "adminui3" }),
        description: i18n.t("clientSubscriptionStore.updateSuccessDescription", { ns: "adminui3" }),
      });
      return true;
    }

    // 3) Cas B : aucun actif, ou création explicitement demandée => INSERTION.
    //
    // Les abonnements actifs que le nouveau RECOUVRE doivent passer en
    // `expired` AVANT l'insertion, sinon l'adhérent se retrouve avec deux
    // abonnements actifs sur le même planning (et la garde serveur de cumul est
    // franchie par le repli direct en table). L'expiration est bloquante : si
    // elle échoue, on n'insère pas.
    //
    // ⚠️ Ce qui a changé, et pourquoi. Ce bloc clôturait l'abonnement actif SANS
    // condition. C'était la seule règle possible tant qu'un adhérent ne pouvait
    // en détenir qu'un ; depuis le cumul par planning, elle rendait la
    // fonctionnalité INATTEIGNABLE depuis l'interface d'administration : ajouter
    // « Séances privées » à un adhérent qui a « Cygne noir » clôturait le Cygne,
    // et l'adhérent n'avait jamais ses deux abonnements. On ne clôture donc plus
    // que ceux dont la couverture recoupe celle du nouveau — exactement le
    // critère qu'appliquera le serveur (`expireOverlappingActiveSubscriptions`),
    // qui expire lui aussi ces abonnements avant d'insérer : ce pré-expire est
    // donc redondant mais sans effet (le serveur ne retrouve rien d'actif à
    // clôturer), et il conserve sa compensation propre si l'insert échoue.
    //
    // Rétrocompatibilité : sans aucune ligne dans `subscription_type_calendars`,
    // tous les abonnements sont universels, tout se recoupe, et on clôture
    // l'actif comme avant. Idem si la lecture des liens échoue — dans le doute
    // on clôture, c'est-à-dire le comportement historique, jamais le cumul.
    const toExpire = await resolveSubscriptionsToExpire(
      activeSubscriptions,
      subscriptionData.subscription_type_id ?? null
    );

    const expired: any[] = [];
    for (const sub of toExpire) {
      try {
        await updateSubscription(sub.id, { status: "expired" }, gymId);
        expired.push(sub);
      } catch (updateError: any) {
        console.error("Échec de l'expiration de l'ancien abonnement :", updateError);
        // Ce qui a déjà été clôturé est remis en l'état : sortir ici en
        // laissant un abonnement `expired` et pas de nouveau créé priverait
        // l'adhérent de ses séances sans que rien ne le lui dise.
        for (const done of expired) {
          try {
            await updateSubscription(done.id, { status: done.status ?? "active" }, gymId);
          } catch (restoreError) {
            console.error("Échec de la restauration après expiration partielle :", restoreError);
          }
        }
        toast({
          title: i18n.t("clientSubscriptionStore.errorTitle", { ns: "adminui3" }),
          description: i18n.t("clientSubscriptionStore.expireOldFailed", { ns: "adminui3", message: updateError?.message ?? updateError }),
          variant: "destructive",
        });
        return false;
      }
    }

    // `id` est retiré : c'est une création, la clé primaire est générée en base.
    const { status, id: _newId, ...rest } = subscriptionData;
    const insertPayload = {
      ...rest,
      user_id: userId,
      gym_id: getUserGymId(),
      status: String(status ?? "active").toLowerCase(),
    };

    try {
      await createSubscription(insertPayload);
    } catch (insertError: any) {
      console.error("Erreur d'insertion abonnement :", insertError);

      // Les anciens abonnements viennent d'être passés en `expired` juste
      // au-dessus : sans compensation, l'adhérent se retrouve SANS abonnement
      // actif (ses séances restantes deviennent inaccessibles) alors qu'on lui
      // annonce seulement « échec de la création ». On les remet donc en l'état.
      let restored = true;
      for (const sub of expired) {
        try {
          await updateSubscription(sub.id, { status: sub.status ?? "active" }, gymId);
        } catch (restoreError) {
          restored = false;
          console.error("Échec de la restauration de l'ancien abonnement :", restoreError);
        }
      }

      toast({
        title: i18n.t("clientSubscriptionStore.errorTitle", { ns: "adminui3" }),
        description: restored
          ? i18n.t("clientSubscriptionStore.createFailed", { ns: "adminui3", message: insertError?.message ?? insertError })
          : i18n.t("clientSubscriptionStore.createFailedRestoreFailed", { ns: "adminui3", message: insertError?.message ?? insertError }),
        variant: "destructive",
      });
      return false;
    }

    toast({
      title: i18n.t("clientSubscriptionStore.updateSuccessTitle", { ns: "adminui3" }),
      description: i18n.t("clientSubscriptionStore.createSuccessDescription", { ns: "adminui3" }),
    });
    return true;
  } catch (err) {
    console.error("Unexpected error updating/creating subscription:", err);
    toast({
      title: i18n.t("clientSubscriptionStore.errorTitle", { ns: "adminui3" }),
      description: i18n.t("clientSubscriptionStore.unexpectedErrorDescription", { ns: "adminui3" }),
      variant: "destructive",
    });
    return false;
  } finally {
    set({ isLoading: false });
  }
},

  /**
   * TOUS les abonnements actifs de l'adhérent dans la salle courante.
   *
   * Un adhérent peut en détenir plusieurs, de plannings différents (par ex.
   * « Planning général » et « Cours Privés »), avec des quotas et des dates de
   * fin indépendants. Ne jamais réduire cette liste à son premier élément :
   * c'est exactement le bug que corrige ce chantier.
   *
   * Lève si la lecture échoue (cf. `getUserSubscriptions`) : un tableau vide
   * signifie donc toujours « le serveur a répondu : aucun abonnement actif ».
   */
  getClientActiveSubscriptionsByUserId: async (userId: string) => {
    const gymId = getUserGymId();
    const subscriptions = await getUserSubscriptions(userId, 'active', gymId);
    return (subscriptions ?? []) as Subscription[];
  },

  /**
   * @deprecated Ne renvoie que le PREMIER abonnement actif et masque donc les
   * autres. Conservé pour les appelants historiques ; tout code neuf doit
   * passer par `getClientActiveSubscriptionsByUserId`.
   */
  getClientActiveSubscriptionByUserId: async (userId:string) => {
    const subscriptions = await get().getClientActiveSubscriptionsByUserId(userId);
    return subscriptions?.[0] as Subscription || null;
  }

}));
